/* eslint-disable */
import React, { forwardRef, useEffect, useState } from "react";
import { FiSearch } from "react-icons/fi";
import ReactPaginate from "react-paginate";

/**
 * The deduction ledger the member pages share — /loans and /mart-deduction show
 * the same five columns over the same paginated shape, so the layout lives here
 * rather than being kept in step by hand in each page.
 *
 * Styled inline, the way the newer member pages are: the Tailwind stylesheet in
 * src/dist is a generated file only rebuilt by the `build-css` watcher, so new
 * utility classes would silently not exist.
 */
export const GREEN = "#2d6a4f";
export const GREEN_DARK = "#1b4332";
export const INK = "#1a3c2a";

export const card = {
  background: "#fff",
  borderRadius: 14,
  border: "1px solid #e8ece9",
  boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
};

export const money = (n) =>
  Number(n || 0).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const DEDUCTION_COLUMNS = [
  { key: "transaction_type", label: "Transaction Type" },
  { key: "period", label: "Period" },
  { key: "original_amount", label: "Original Amount", numeric: true },
  { key: "deduction", label: "Deduction", numeric: true },
  { key: "balance", label: "Balance", numeric: true },
];

export function ToolbarButton({ icon: Icon, label, onClick, to, tone, disabled }) {
  const solid = tone === "solid";
  const style = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 14px",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    textDecoration: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
    border: solid ? "1px solid transparent" : "1px solid #dde3df",
    background: solid ? GREEN : "#fff",
    color: solid ? "#fff" : "#3c4a43",
    whiteSpace: "nowrap",
  };

  const content = (
    <>
      {Icon ? <Icon size={16} /> : null}
      {label}
    </>
  );

  // `to` renders whatever the caller passes (a react-router Link element), so
  // this component doesn't have to know about routing.
  if (to) return React.cloneElement(to, { style }, content);

  return (
    <button type="button" onClick={disabled ? undefined : onClick} disabled={disabled} style={style}>
      {content}
    </button>
  );
}

function useIsMobile(breakpoint = 820) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

const DeductionsView = forwardRef(function DeductionsView(
  {
    title,
    subtitle,
    tableTitle,
    rows,
    columns = DEDUCTION_COLUMNS,
    searchPlaceholder = "Search by period e.g. JAN22",
    onSearch,
    actions,
    emptyText = "Nothing recorded yet.",
    pageCount = 0,
    onPageChange,
    totalRecords,
  },
  ref
) {
  const isMobile = useIsMobile();
  const list = rows || [];

  const cellValue = (row, column) =>
    column.numeric ? `¢${money(row?.[column.key])}` : row?.[column.key] || "—";

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", paddingBottom: 32 }}>
      {/* Header */}
      <div
        style={{
          ...card,
          background: `linear-gradient(135deg, ${GREEN} 0%, ${GREEN_DARK} 100%)`,
          border: "none",
          color: "#fff",
          padding: "22px 24px",
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{title}</h2>
        {subtitle ? (
          <p style={{ margin: "6px 0 0", fontSize: 13, opacity: 0.85 }}>{subtitle}</p>
        ) : null}
      </div>

      {/* Toolbar */}
      <div
        style={{
          ...card,
          padding: 14,
          marginBottom: 16,
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ position: "relative", flex: "1 1 260px", minWidth: 200 }}>
          <FiSearch
            size={16}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#9aa49e",
            }}
          />
          <input
            type="search"
            name="searchDeductions"
            placeholder={searchPlaceholder}
            onChange={onSearch}
            style={{
              width: "100%",
              padding: "10px 12px 10px 36px",
              borderRadius: 10,
              border: "1px solid #dde3df",
              outline: "none",
              fontSize: 13,
              background: "#fbfcfb",
            }}
          />
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div>
      </div>

      {/* Records */}
      <div ref={ref} style={{ ...card, overflow: "hidden" }}>
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid #eef1ef",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: INK }}>
            {tableTitle || title}
          </h3>
          <span style={{ fontSize: 12, color: "#8b968f" }}>
            {totalRecords ?? list.length} record{(totalRecords ?? list.length) === 1 ? "" : "s"}
          </span>
        </div>

        {isMobile ? (
          /* ── Mobile: card list ── */
          <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            {list.map((row, index) => (
              <div
                key={index}
                style={{
                  border: "1px solid #e8ece9",
                  borderRadius: 10,
                  padding: 12,
                  background: "#fbfcfb",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: 13, color: INK }}>
                    {row?.transaction_type || "—"}
                  </span>
                  <span style={{ fontWeight: 800, fontSize: 14, color: GREEN }}>
                    ¢{money(row?.deduction)}
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0,1fr))",
                    gap: 6,
                    fontSize: 11.5,
                  }}
                >
                  {[
                    ["Period", row?.period || "—"],
                    ["Original", `¢${money(row?.original_amount)}`],
                    ["Balance", `¢${money(row?.balance)}`],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <p style={{ margin: 0, color: "#98a29c" }}>{label}</p>
                      <p style={{ margin: 0, color: "#3c4a43", fontWeight: 600 }}>{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {!list.length && (
              <p style={{ textAlign: "center", padding: 30, color: "#98a29c", fontSize: 13 }}>
                {emptyText}
              </p>
            )}
          </div>
        ) : (
          /* ── Desktop: table ── */
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f7f9f8" }}>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      style={{
                        padding: "11px 18px",
                        textAlign: column.numeric ? "right" : "left",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                        color: "#78857e",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((row, index) => (
                  <tr key={index} style={{ borderTop: "1px solid #f0f3f1" }}>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        style={{
                          padding: "11px 18px",
                          fontSize: 13,
                          color: "#3c4a43",
                          textAlign: column.numeric ? "right" : "left",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {cellValue(row, column)}
                      </td>
                    ))}
                  </tr>
                ))}

                {!list.length && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{ padding: 40, textAlign: "center", color: "#98a29c", fontSize: 13 }}
                    >
                      {emptyText}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {list.length > 0 && pageCount > 1 && (
        <ReactPaginate
          breakLabel="..."
          nextLabel=">"
          onPageChange={onPageChange}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="<"
          renderOnZeroPageCount={null}
          containerClassName="pagination"
          pageLinkClassName="page-num"
          previousLinkClassName="page-num"
          nextLinkClassName="page-num"
          activeLinkClassName="active"
        />
      )}
    </div>
  );
});

export default DeductionsView;
